import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import EnquiryDialog from "@/components/EnquiryDialog";

const NAV = [
  { label: "Courses", to: "/courses" },
  { label: "Workshops", to: "/workshops/intro-to-ethical-hacking" },
  { label: "About", to: "/about" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [enquire, setEnquire] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b-2"
      style={{ borderColor: "#5C1B93" }}
    >
      <div className="w-full mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 shrink-0">
            <img src="/fluenex-symbol.svg" alt="" aria-hidden="true" className="h-9 md:h-10 w-auto object-contain" />
            <span className="flex flex-col leading-none">
              <span className="font-display font-bold text-lg md:text-xl tracking-tight text-ink">
                Fluenex <span className="font-normal">Academy</span>
              </span>
              <span className="font-sans text-[10px] md:text-[11px] font-semibold uppercase tracking-[0.14em] text-graphite mt-0.5">
                Proof over Paper
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="font-sans text-[15px] font-semibold text-ink hover:text-violet transition-colors"
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-6">
            <button
              data-testid="nav-signin"
              onClick={() => navigate(user && user.role === "admin" ? "/admin" : "/login")}
              className="font-sans text-[15px] font-semibold text-graphite hover:text-violet transition-colors"
            >
              {user && user.role === "admin" ? "Dashboard" : "Sign In"}
            </button>
            <button
              data-testid="nav-enquire"
              onClick={() => setEnquire(true)}
              className="font-sans text-[15px] font-bold rounded-full bg-terracotta text-white px-6 py-2.5 shadow-sm hover:bg-terracottahover hover:shadow-glowwarm transition duration-300"
            >
              Enquire
            </button>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 -mr-2 text-ink"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t bg-white"
          style={{ borderColor: "#5C1B93" }}
        >
          <div className="px-4 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="font-sans text-base font-medium text-ink">
                {n.label}
              </Link>
            ))}
            <button
              onClick={() => navigate(user && user.role === "admin" ? "/admin" : "/login")}
              className="text-left font-sans text-base font-medium text-graphite"
            >
              {user && user.role === "admin" ? "Dashboard" : "Sign In"}
            </button>
            <button
              data-testid="nav-enquire-mobile"
              onClick={() => { setOpen(false); setEnquire(true); }}
              className="w-full text-center font-sans text-base font-semibold rounded-full bg-terracotta text-white px-6 py-3"
            >
              Enquire
            </button>
          </div>
        </div>
      )}

      <EnquiryDialog open={enquire} onClose={() => setEnquire(false)} course={null} source="nav" />
    </header>
  );
}
